import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  afterNextRender,
  AfterViewInit,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  PLATFORM_ID,
  signal,
  ViewChild,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Firestore } from '@angular/fire/firestore';
import { NavigationEnd, Router, RouterLink } from '@angular/router';

import {
  ChannelWithDocId,
  getChannelNavigation,
} from '@yoimo/client-sdk/channels';
import { EventWithDocId } from '@yoimo/client-sdk/events';
import { getNamedTagFromSourceList } from '@yoimo/client-sdk/named-tags';
import {
  ChannelNavigation,
  EventListSource,
  LinkNavigationResource,
  NamedTag,
  NamedTagType,
} from '@yoimo/interfaces';
import {
  BreakpointsService,
  ButtonModule,
  CardComponent,
  CardModule,
  IconModule,
  MenuComponent,
  SkeletonCardComponent,
  TagCardComponent,
  WindowService,
} from '@yoimo/joymo-ui';

import { combineLatest, filter, map, Observable, of, switchMap } from 'rxjs';

import {
  EventsService,
  PlatformService,
  ScopeService,
  UserOnboardingService,
} from '@core/services';
import { environment } from '@environment';

import { HelpComponent, InfoBlockComponent } from 'shared-lib';
import { getMailToLink, ListAsMapPipe } from 'shared-lib/core';

export type UIRootNavigationResource =
  | NamedTagGroup
  | EventGroup
  | ListGroup
  | LinkNavigationResource;

export type EventGroup = {
  type: 'EVENTS';
  label?: string;
  sources: { label: string; items$: Observable<EventWithDocId[]> }[];
  showEmptyState$: Observable<boolean>;
};

export type NamedTagGroup = {
  type: 'NAMED_TAGS';
  tagType: NamedTagType;
  label?: string;
  groupBy?: string;
  items$: Observable<NamedTag[]>;
};

export type ListGroup = {
  type: 'MENU';
  label: string;
  items: LinkNavigationResource[];
};

@Component({
  standalone: true,
  selector: 'joymo-drawer',
  imports: [
    CommonModule,
    RouterLink,
    IconModule,
    MenuComponent,
    ButtonModule,
    HelpComponent,
    ListAsMapPipe,
    TagCardComponent,
    CardComponent,
    CardModule,
    InfoBlockComponent,
    SkeletonCardComponent,
  ],
  templateUrl: './drawer.component.html',
  styleUrls: ['./drawer.component.scss'],
})
export class DrawerComponent implements OnInit, AfterViewInit {
  private readonly platformId = inject(PLATFORM_ID);
  /** Internal links */
  readonly links: { icon: string; label: string; route: string }[] = [];
  /** Dynamic navigation options */
  private allNavigationResources = signal<UIRootNavigationResource[]>([]);
  activeNavigation?: UIRootNavigationResource;

  // More menu variables
  private visibleElements = signal<Set<Element>>(new Set());
  private hiddenElements = signal<Set<Element>>(new Set());
  drawerClone = viewChild<ElementRef<HTMLElement>>('drawerClone');
  private cloneContainer: HTMLElement | null = null;
  private intersectionObserver: IntersectionObserver | null = null;
  private static idCounter = 0;
  readonly moreMenuButtonId = `moreMenu-${DrawerComponent.idCounter++}`;

  // Props
  @Input() canSubscribe$?: Observable<boolean>;
  @Output() onSubscribeClick = new EventEmitter<void>();

  // Refs
  @ViewChild(MenuComponent) private readonly menu?: MenuComponent;
  @ViewChild('drawer', { read: ElementRef }) drawer!: ElementRef;
  private readonly destroyRef = inject(DestroyRef);

  readonly NO_EVENTS_LABEL = $localize`:@@navigationNoEvents:No events found`;

  /** @deprecated Should react to `channel$` */
  readonly homepageUrl = this.scopeService.getHomepageUrl();
  readonly channel$ = this.scopeService.listenToChannel();
  /** @deprecated Duplicated from FooterComponent - componentize? */
  readonly supportLink$: Observable<string | null>;
  /** @deprecated Duplicated from FooterComponent - componentize? */
  readonly defaultSupportEmail = environment.joymo.defaultSupportEmail
    ? getMailToLink(environment.joymo.defaultSupportEmail)
    : null;

  // Navigation items that are currently visible
  readonly navigationResources = computed(() => {
    if (this.platformService.isServer()) return this.allNavigationResources();

    const hiddenElements = new Set(this.hiddenElements());
    const allNavs = [...this.allNavigationResources()];

    if (hiddenElements.size === 0 || allNavs.length === 0) {
      return allNavs;
    }
    const drawerCloneEl = this.drawerClone()?.nativeElement;
    if (!drawerCloneEl) return allNavs;

    const hiddenIndices = this.getHiddenIndices(drawerCloneEl, hiddenElements);

    return allNavs.filter((_, index) => !hiddenIndices.has(index));
  });

  // Navigation items that are moved to the "More" menu
  readonly moreNavigationResources = computed(() => {
    if (this.platformService.isServer()) return [];

    const hiddenElements = this.hiddenElements();
    const allNavs = this.allNavigationResources();

    if (hiddenElements.size === 0 || allNavs.length === 0) {
      return [];
    }

    const drawerCloneEl = this.drawerClone()?.nativeElement;
    if (!drawerCloneEl) return [];

    const hiddenIndices = this.getHiddenIndices(drawerCloneEl, hiddenElements);

    return allNavs.filter((_, index) => hiddenIndices.has(index));
  });

  constructor(
    protected platformService: PlatformService,
    protected breakpointService: BreakpointsService,
    private scopeService: ScopeService,
    private eventService: EventsService,
    private fs: Firestore,
    private windowService: WindowService,
    private router: Router,
    private userOnboardingService: UserOnboardingService
  ) {
    this.supportLink$ = this.channel$.pipe(
      map(({ branding }) => {
        const { contactInformation } = branding;
        if (!contactInformation?.supportEmail) return this.defaultSupportEmail;
        return getMailToLink(contactInformation?.supportEmail);
      })
    );
  }

  ngOnInit(): void {
    if (this.platformService.isServer()) return;

    this.channel$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((channel) => {
        const channelNav = getChannelNavigation(channel);
        const parsedChannelNav = this.getNavigationResources(
          channel,
          channelNav
        );

        if (channelNav.some((nav) => nav.type === 'CALENDAR')) {
          this.links.push({
            icon: 'calendar',
            label: $localize`:@@drawerCalendarLink:Calendar`,
            route: 'calendar',
          });
        }

        this.allNavigationResources.set(parsedChannelNav);
        // nextTick needed to get correct width of the menu items
        setTimeout(() => this.createDrawerClone());
      });
  }

  ngAfterViewInit(): void {
    this.router.events
      .pipe(
        filter((event) => {
          return event instanceof NavigationEnd;
        }),
        switchMap((_) => this.breakpointService.isMobile$()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((isMobile) => {
        this.userOnboardingService.addHostForFeature(
          'PAGES',
          this.drawer,
          'bottom',
          isMobile ? 'left' : 'center'
        );
      });

    if (!this.platformService.isServer()) {
      setTimeout(() => {
        const drawerCloneEl = this.drawerClone()?.nativeElement;
        if (drawerCloneEl) {
          this.setContainerElement(drawerCloneEl);
        }
      }, 100);
    }
  }

  setContainerElement(element: HTMLElement | null): void {
    if (!isPlatformBrowser(this.platformId) || !element) return;

    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }

    this.cloneContainer = element;
    this.setupIntersectionObserver();
  }

  private setupIntersectionObserver(): void {
    if (!this.cloneContainer) return;

    const children = Array.from(this.cloneContainer.children);

    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        const visible = new Set(this.visibleElements());
        const hidden = new Set(this.hiddenElements());

        entries.forEach((entry) => {
          if (entry.intersectionRatio >= 0.99) {
            visible.add(entry.target);
            hidden.delete(entry.target);
          } else {
            visible.delete(entry.target);
            hidden.add(entry.target);
          }
        });

        this.visibleElements.set(visible);
        this.hiddenElements.set(hidden);
      },
      {
        root: this.cloneContainer.parentElement,
        threshold: [0, 0.5, 0.99, 1], // Multiple thresholds for fast resize detection
        rootMargin: '0px -30px 0px 0px', // So it won't overflow Buttons
      }
    );

    children.forEach((child) => {
      this.intersectionObserver?.observe(child);
    });
  }

  /**
   Creates a container which will be the clone of the drawer that will be used by the IntersectionObserver
   */
  private createDrawerClone(): void {
    if (!this.drawer?.nativeElement || !this.drawerClone()?.nativeElement)
      return;

    const drawerElChildren = Array.from(
      this.drawer.nativeElement.children
    ) as HTMLElement[];

    drawerElChildren.forEach((el) => {
      if (el.id === this.moreMenuButtonId) return;

      this.createMenuItemClone(el);
    });
  }

  /**
   Creates width/height clone of the menu item to be used by the IntersectionObserver.
   Needed for accurate width clone of the actual menu items.
   */
  private createMenuItemClone(el: HTMLElement): void {
    const { height, width } = el.getBoundingClientRect();
    const span = document.createElement('span');
    span.style.height = `${height}px`;
    span.style.width = `${width}px`;
    span.style.minWidth = `${width}px`;
    span.style.flexShrink = '0';
    span.style.display = 'inline-block';
    this.drawerClone()?.nativeElement.appendChild(span);
  }

  closeDrawer(): void {
    this.activeNavigation = undefined;
    this.menu?.close();
  }

  setActiveNavigation(option: UIRootNavigationResource): void {
    if (option.type === 'LINK') {
      this.navigateToLink(option, this.homepageUrl);
      return;
    }
    this.activeNavigation = option;
  }

  /** @deprecated Can be achieved by template using regular <a> tags or [routerLink] */
  navigateToLink(item: LinkNavigationResource, baseUrl: string | null): void {
    switch (item.linkType) {
      case 'url':
        item.external
          ? this.windowService.window?.open(item.url, '_blank')
          : (this.windowService.document.location.href = item.url);
        break;
      case 'tag':
        this.router.navigate([baseUrl, 'tags', item.tagName]);
        break;
      case 'event':
        this.router.navigate([baseUrl, 'events', item.eventId]);
        break;
      case 'video':
        this.router.navigate([baseUrl, 'videos', item.videoId]);
        break;
      case 'page':
        this.router.navigate([baseUrl, 'pages', item.pageId]);
        break;
    }
    this.closeDrawer();
  }

  private getEventNav(
    label: string | undefined,
    sources: { label: string; source: EventListSource }[]
  ): EventGroup {
    const eventGroup: EventGroup = {
      type: 'EVENTS',
      label: label || $localize`:@@navigationEventsLabel:Events`,
      sources: sources.map(({ label, source }) => ({
        label,
        items$: this.scopeService.scope$.pipe(
          switchMap(({ scope, scopeId }) =>
            this.eventService.getEvents$(source, { scope, scopeId })
          )
        ),
      })),
      showEmptyState$: of(false),
    };

    const groupItems: Observable<EventWithDocId[]>[] = eventGroup.sources.map(
      (group) => group.items$
    );

    eventGroup.showEmptyState$ = combineLatest(groupItems).pipe(
      map((res) => res.every((g) => g.length === 0))
    );

    return eventGroup;
  }

  private getNavigationResources(
    channel: ChannelWithDocId,
    navigation: ChannelNavigation
  ): UIRootNavigationResource[] {
    return navigation.reduce<UIRootNavigationResource[]>((res, item) => {
      if (item.type === 'MENU' || item.type === 'LINK') return [...res, item];
      if (item.type === 'EVENTS') {
        const eventNav = this.getEventNav(item.label, item.listSources || []);
        return !eventNav.sources.length ? res : [...res, eventNav];
      }
      if (item.type === 'NAMED_TAGS') {
        return [
          ...res,
          {
            type: 'NAMED_TAGS',
            label: item.label || $localize`:@@navigationTagsLabel:Tags`,
            tagType: item.tagType,
            groupBy: item.tagType,
            items$: getNamedTagFromSourceList(
              this.fs,
              { type: item.tagType },
              { scope: 'CHANNEL', scopeId: channel.docId }
            ),
          },
        ];
      }
      return res;
    }, []);
  }

  /*
  Used by More menu when opening and returning inside the More Menu Drawer
  */
  back(event?: Event): void {
    event?.stopPropagation();
    this.activeNavigation = undefined;
  }

  /*
    Helper. Looks at the drawerClone hidden elements and returns the indices of the hidden navigation items.
  */
  private getHiddenIndices(
    drawerCloneEl: HTMLElement,
    hiddenElements: Set<Element>
  ): Set<number> {
    const children = Array.from(drawerCloneEl.children);
    const hiddenIndices = new Set<number>();

    hiddenElements.forEach((hiddenEl) => {
      const index = children.indexOf(hiddenEl);
      if (index !== -1) {
        const navIndex = index - this.links.length;
        if (navIndex >= 0 && navIndex < this.allNavigationResources().length) {
          hiddenIndices.add(navIndex);
        }
      }
    });
    return hiddenIndices;
  }
}
